import { useCallback, useContext, useMemo, useRef } from "react";
import { Task } from "@common/types/task";
import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { isEqual } from "@common/helpers/comparison";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { TablePageSubHeader } from "@design-system/TablePageSubHeader/TablePageSubHeader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { useTransactionsStatisticsQuery } from "@components/transactions/useTransactionsStatisticsQuery";
import {
    DEFAULT_FILTERS,
    FiltersChangeFn,
} from "@components/transactions/filters/lib";
import { TransactionsTaskView } from "../Transactions/TransactionsTaskView";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { AccrualReviewProvider } from "@components/accounting/accrualReview/AccrualReviewContext";
import { ACCRUAL_REVIEW_QUERY_KEY } from "@components/transactions/useTransactionsQuery";
import { LOAD_MORE_PAGINATION_MODE } from "@hooks/paginationMode";

import { AccrualReviewTaskActionBar } from "./AccrualReviewTaskActionBar";
import {
    AccrualReviewTaskFocusProvider,
    useAccrualReviewTaskFocusControls,
} from "./AccrualReviewTaskFocusContext";
import { AccrualReviewTabs } from "./AccrualReviewTabs";
import { useAccrualReviewAvailableFilters } from "./useAccrualReviewAvailableFilters";
import { useAccrualReviewTaskViewConfig } from "./useAccrualReviewTaskViewConfig";
import {
    AccrualReviewTab,
    getAccrualReviewTab,
    getAccrualReviewTabFilters,
    getAccrualReviewTableView,
    getAccrualReviewTaskDefaultView,
    getReviewStatusLabel,
    mergeTaskFilters,
} from "./accrualReviewTaskView";
import { useTransactionRefContextMaybe } from "../../TransactionRefContext";
import styles from "./AccrualReviewTaskBody.module.scss";

interface AccrualReviewTaskBodyProps {
    selectedTask: Task;
}

interface AccrualReviewTaskBodyContentProps extends AccrualReviewTaskBodyProps {
    defaultView: TransactionView;
}

/**
 * Body of a `TaskType.ACCRUAL_REVIEW` task. Mirrors the standalone Accrual
 * Review page layout: search, interactive filters and To review / Reviewed
 * tabs layered on top of the task's fixed constraints (entity, accrual
 * suggestion bucket, completed status). Enriches rows with accrual match
 * suggestion/match details so accrual-specific cells render, and wires the
 * task action bar into the sticky bottom command island on To review.
 */
const AccrualReviewTaskBodyContent = ({
    selectedTask,
    defaultView,
}: AccrualReviewTaskBodyContentProps) => {
    const trxRefCtx = useTransactionRefContextMaybe();
    const availableFilters = useAccrualReviewAvailableFilters();
    const transactionsViewConfig = useAccrualReviewTaskViewConfig();
    const { onFocusedTransactionChange, openCellActionRef } =
        useAccrualReviewTaskFocusControls();

    const { transactionCount, filters, currentView } = useContext(
        PaginatedTransactionsDataContext,
    );
    const { setCurrentFilters, setShownTransactionId, setCurrentView } =
        useContext(PaginatedTransactionsActionsContext);

    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;

    const activeTab = getAccrualReviewTab(filters);
    const isToReviewTab = activeTab === AccrualReviewTab.TO_REVIEW;

    const initialFilters = useMemo(
        () => selectedTask.view?.filters ?? {},
        [selectedTask.view?.filters],
    );
    // Task progress, counted from the task's own filters so the tab, search
    // and user filters don't change it.
    const taskEntityIds = initialFilters.entityIds ?? filters.entityIds;
    const { data: totalStats } = useTransactionsStatisticsQuery({
        filters: {
            ...initialFilters,
            entityIds: taskEntityIds,
            isNotAccrualReviewed: undefined,
            isAccrualReviewed: undefined,
        },
    });
    const { data: remainingStats } = useTransactionsStatisticsQuery({
        filters: { ...initialFilters, entityIds: taskEntityIds },
    });
    const subHeaderLabel = getReviewStatusLabel({
        total: totalStats?.count,
        remainingCount: remainingStats?.count,
    });

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const newFilters = mergeTaskFilters(
                currentFiltersRef.current,
                value,
            );
            if (isEqual(newFilters, currentFiltersRef.current)) {
                return;
            }
            setShownTransactionId(undefined);
            setCurrentFilters(newFilters);
        },
        [setShownTransactionId, setCurrentFilters],
    );

    const handleSearchChange = useCallback(
        (value: string) => handleFiltersChange({ search: value || undefined }),
        [handleFiltersChange],
    );

    const handleTabChange = useCallback(
        (tab: AccrualReviewTab) =>
            handleFiltersChange(getAccrualReviewTabFilters(tab)),
        [handleFiltersChange],
    );

    // ColumnSettings writes the view it gets back into `currentView`, so it
    // gets the To review options and Reviewed's `readonly` never sticks.
    const viewWithSelection = useMemo(
        () =>
            getAccrualReviewTableView({
                view: currentView,
                tab: AccrualReviewTab.TO_REVIEW,
            }),
        [currentView],
    );
    const tableView = useMemo(
        () => getAccrualReviewTableView({ view: currentView, tab: activeTab }),
        [currentView, activeTab],
    );

    const isSpecificTransactionTask =
        !!currentView.filters?.transactionIds?.length;
    let emptyMessage = "No more transactions matching the task definition";
    if (activeTab === AccrualReviewTab.REVIEWED) {
        emptyMessage = "No reviewed transactions";
    } else if (isSpecificTransactionTask) {
        emptyMessage = "No transactions associated with this task";
    }

    const renderFooter = useCallback(
        () => <AccrualReviewTaskActionBar initialFilters={initialFilters} />,
        [initialFilters],
    );

    const handleViewChange = useCallback(
        (view: TransactionView) => {
            setCurrentView(view);
        },
        [setCurrentView],
    );

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={filters}
            totalTransactions={transactionCount}
            onUpdated={() => {}}
        >
            <div className={styles.container}>
                {subHeaderLabel && (
                    <TablePageSubHeader borderBottom>
                        {subHeaderLabel}
                    </TablePageSubHeader>
                )}
                <TablePageHeader
                    search={filters.search}
                    searchPlaceholder="Search"
                    onSearchChange={handleSearchChange}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    defaultFilters={DEFAULT_FILTERS}
                    availableFilters={availableFilters}
                    hideClearButton
                    isFullPage
                    borderBottom
                >
                    <ColumnSettings<TransactionView>
                        view={viewWithSelection}
                        viewConfig={transactionsViewConfig}
                        defaultView={defaultView}
                        onViewChange={handleViewChange}
                    />
                    <AccrualReviewTabs
                        value={activeTab}
                        onValueChange={handleTabChange}
                    />
                </TablePageHeader>
                <section className={styles.body}>
                    <TransactionsTaskView
                        view={tableView}
                        viewConfigOverride={transactionsViewConfig}
                        className={styles.table}
                        selectedTransactionId={
                            trxRefCtx?.focusedTransactionId ?? undefined
                        }
                        footer={isToReviewTab ? renderFooter : undefined}
                        isEnterSelectionDisabled={isToReviewTab}
                        isNoteShortcutDisabled={isToReviewTab}
                        onFocusedTransactionChange={onFocusedTransactionChange}
                        openCellActionRef={openCellActionRef}
                        emptyView={() => <EmptyState header={emptyMessage} />}
                    />
                </section>
            </div>
        </TransactionsBulkActionsContextProvider>
    );
};

export const AccrualReviewTaskBody = ({
    selectedTask,
}: AccrualReviewTaskBodyProps) => {
    if (!selectedTask.view) {
        return null;
    }

    const parsedDefaultView = TransactionViewSchema.safeParse(
        selectedTask.view,
    );
    if (!parsedDefaultView.success) {
        return null;
    }

    const defaultView = getAccrualReviewTaskDefaultView(parsedDefaultView.data);

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={defaultView}
            paginationMode={LOAD_MORE_PAGINATION_MODE}
            includeAccrualMatchDetails
            queryKeyPrefix={ACCRUAL_REVIEW_QUERY_KEY}
            key={selectedTask.id}
        >
            <AccrualReviewProvider
                confirmDisplay="withChangeCount"
                hasCellActionOpeners
                ledgerDisplay="button"
            >
                <AccrualReviewTaskFocusProvider>
                    <AccrualReviewTaskBodyContent
                        selectedTask={selectedTask}
                        defaultView={defaultView}
                    />
                </AccrualReviewTaskFocusProvider>
            </AccrualReviewProvider>
        </PaginatedTransactionsTableContextProvider>
    );
};
